import { useEffect, useRef, useState } from "react";

// MentionsTextarea — Feb 2026.
//
// Drop-in <textarea> replacement that supports @mention autocomplete.
//
// Behavior:
// - Type "@" anywhere → a floating dropdown appears with matching members.
// - Continue typing to filter; arrow keys to navigate; Enter or Tab to insert.
// - The inserted mention becomes literal text like "@Greg Norton" in the
//   textarea (no fancy DOM chips, keeps it plain-text simple).
// - `onMentionsChange` reports the resolved user_ids of currently-mentioned
//   members. Detection is conservative — we only call out a member as
//   mentioned if their FULL display name (or email) appears verbatim after
//   an "@".
//
// Props:
//   value            (string)              — controlled text content
//   onChange         (string => void)      — text content updates
//   onMentionsChange (string[] => void)    — user_ids of @mentioned members
//   members          ([{id, name, email}]) — candidates for autocomplete
//   placeholder, rows, disabled, maxLength, onKeyDown, ...rest pass through
//
// All other textarea props (className, data-testid, etc.) pass through.

export default function MentionsTextarea({
  value,
  onChange,
  onMentionsChange,
  members = [],
  placeholder,
  rows = 3,
  disabled = false,
  maxLength,
  onKeyDown,
  className = "",
  wrapperClassName = "",
  ...rest
}) {
  const textareaRef = useRef(null);
  const [query, setQuery] = useState(null); // null = closed, string = open
  const [queryStart, setQueryStart] = useState(0); // index of '@' in value
  const [highlightIndex, setHighlightIndex] = useState(0);

  // Recompute resolved mentions whenever value or members change.
  useEffect(() => {
    if (!onMentionsChange) return;
    const resolved = resolveMentions(value, members);
    onMentionsChange(resolved);
  }, [value, members, onMentionsChange]);

  const candidates = (() => {
    if (query === null) return [];
    const q = query.toLowerCase().trim();
    return members
      .filter((m) => {
        const name = (m.name || "").toLowerCase();
        const email = (m.email || "").toLowerCase();
        if (!q) return true;
        return name.includes(q) || email.includes(q);
      })
      .slice(0, 6);
  })();

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    // Look back from caret for an unbroken @… token
    const caret = e.target.selectionStart || newValue.length;
    const upToCaret = newValue.slice(0, caret);
    const atMatch = /@([^\s@]{0,40})$/.exec(upToCaret);
    if (atMatch) {
      const startIdx = caret - atMatch[0].length;
      setQuery(atMatch[1]);
      setQueryStart(startIdx);
      setHighlightIndex(0);
    } else {
      setQuery(null);
    }
  };

  const handleKeyDown = (e) => {
    if (query !== null && candidates.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % candidates.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((i) => (i - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(candidates[highlightIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setQuery(null);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  const insertMention = (member) => {
    if (!member) return;
    const label = member.name || member.email || "Unknown";
    const before = value.slice(0, queryStart);
    const after = value.slice(queryStart + (query?.length ?? 0) + 1); // +1 = the '@'
    const inserted = `@${label} `;
    const newValue = before + inserted + after;
    onChange(newValue);
    setQuery(null);
    // Defer caret repositioning until React applies new value
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + inserted.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  return (
    <div className={`relative ${wrapperClassName}`}>
      <textarea
        {...rest}
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        maxLength={maxLength}
        className={className}
      />
      {query !== null && candidates.length > 0 && (
        <div
          data-testid="mention-dropdown"
          className="absolute left-2 right-2 -bottom-1 translate-y-full z-30 bg-zinc-950 border border-zinc-700 rounded-sm shadow-xl py-1 max-h-56 overflow-y-auto"
        >
          {candidates.map((m, i) => {
            const isActive = i === highlightIndex;
            return (
              <button
                key={m.id}
                type="button"
                data-testid={`mention-option-${m.id}`}
                onMouseDown={(e) => {
                  // mouseDown not click — click happens after blur, which
                  // would clear the dropdown.
                  e.preventDefault();
                  insertMention(m);
                }}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                  isActive ? "bg-amber-500/15 text-zinc-100" : "text-zinc-300 hover:bg-zinc-900"
                }`}
              >
                <span className="text-xs text-zinc-500 font-mono w-4 text-right">@</span>
                <span className="flex-1 truncate">
                  {m.name || m.email || "Unknown"}
                </span>
                {m.name && m.email && (
                  <span className="text-[10px] text-zinc-600 truncate ml-2 max-w-[140px]">
                    {m.email}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Detect every @-mention in `text` that exactly matches a member's name or
// email. Returns the list of resolved user_ids (deduped, stable order).
//
// We intentionally require an exact full-name (or email) match — so a stray
// "@hey" doesn't accidentally mention a person whose name starts with "h."
// Multi-word names work because we try the longest-name-first strategy.
export function resolveMentions(text, members) {
  if (!text || !members || members.length === 0) return [];
  const sortedByLen = [...members].sort(
    (a, b) => (b.name || "").length - (a.name || "").length,
  );
  const hits = new Set();
  for (const m of sortedByLen) {
    const candidates = [m.name, m.email].filter(Boolean);
    for (const candidate of candidates) {
      // \b on both sides; case-insensitive. Escape regex special chars.
      const safe = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`(^|\\s)@${safe}(?=\\s|$|[.,!?;:])`, "i");
      if (re.test(text)) {
        hits.add(m.id);
        break;
      }
    }
  }
  return Array.from(hits);
}

// Render text with @mentions visually highlighted. Used by both the comment
// list and the Discussion feed for display.
export function renderMentions(text, members = []) {
  if (!text) return null;
  if (!members.length) return linkifyText(text);
  // Sort by longest name first so "Greg Norton" beats "Greg" in greedy match.
  const sortedByLen = [...members].sort(
    (a, b) => (b.name || "").length - (a.name || "").length,
  );
  const tokens = [];
  let cursor = 0;
  while (cursor < text.length) {
    const atIdx = text.indexOf("@", cursor);
    if (atIdx === -1) {
      tokens.push({ kind: "text", value: text.slice(cursor) });
      break;
    }
    if (atIdx > cursor) {
      tokens.push({ kind: "text", value: text.slice(cursor, atIdx) });
    }
    // Find which member matches starting at atIdx+1
    let matched = null;
    for (const m of sortedByLen) {
      const candidates = [m.name, m.email].filter(Boolean);
      for (const candidate of candidates) {
        if (
          text.slice(atIdx + 1, atIdx + 1 + candidate.length).toLowerCase() ===
          candidate.toLowerCase()
        ) {
          // Boundary check
          const after = text[atIdx + 1 + candidate.length];
          if (!after || /\s|[.,!?;:]/.test(after)) {
            matched = { member: m, label: candidate };
            break;
          }
        }
      }
      if (matched) break;
    }
    if (matched) {
      tokens.push({ kind: "mention", label: matched.label, member: matched.member });
      cursor = atIdx + 1 + matched.label.length;
    } else {
      tokens.push({ kind: "text", value: text[atIdx] });
      cursor = atIdx + 1;
    }
  }
  return tokens.map((tk, i) => {
    if (tk.kind === "mention") {
      return (
        <span
          key={i}
          className="text-amber-400 bg-amber-500/10 rounded-sm px-1 py-0.5 font-medium"
          title={tk.member.email || ""}
        >
          @{tk.label}
        </span>
      );
    }
    return <span key={i}>{linkifyText(tk.value)}</span>;
  });
}

// Split a plain-text run into text + <a> nodes for any embedded URLs.
// Matches http(s):// and www. prefixes. Trailing punctuation (.,!?;:)])
// is left outside the link so "see https://x.com/foo." doesn't include the
// period in the href. Rendered links open in a new tab with safe rel attrs.
const URL_REGEX = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCT = /[.,!?;:)\]}>'"]+$/;

function linkifyText(text) {
  if (!text || typeof text !== "string") return text;
  const parts = [];
  let last = 0;
  let match;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    let url = match[0];
    let trailing = "";
    const tp = url.match(TRAILING_PUNCT);
    if (tp) {
      trailing = tp[0];
      url = url.slice(0, -trailing.length);
    }
    if (!url) continue;
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const href = url.startsWith("http") ? url : `https://${url}`;
    parts.push(
      <a
        key={`lnk-${match.index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-amber-400 underline decoration-amber-500/40 hover:decoration-amber-400 hover:text-amber-300 break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    if (trailing) parts.push(trailing);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}
